// Angular
import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
// Store
import {Store} from '@ngxs/store';
import {SetRulesCheckedRules, SetAvailableRuleSet} from '../state/action/rules.actions';
// Model
import {DOCUMENT_RULES_UI_SECTIONS} from '../../model/quality.constant';
import {RULE_MESSAGE_TYPE, RULE_STATUS} from '../model/constants';
import { FeedbackPayload } from '../model/feedback/feedback.model';
import { RuleCommentModel, RuleStipModel } from '../model/rule.model';
import { PostRevisionModel } from '../model/post-revision.model';
// Service
import {
	UpdateRulesChecked,
	UpdateOrderRulesChecked,
	ObjectUtils,
	UserContextState,
	SystemApiService
} from '@cv-invision/core';
// Other
import {uniqBy} from 'lodash';
import {forkJoin, Observable} from 'rxjs';
import {map, share} from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class RuleService {
	constructor(
		private store: Store,
		private systemApiService: SystemApiService,
		private httpClient: HttpClient
	) {}

	/**
	 * GET - Returns the set of rules for a particular document, can also be filtered by UI Section.
	 * @param orderId: number
	 * @param documentId: string
	 * @param uiSection: @see DOCUMENT_RULES_UI_SECTIONS
	 */
	getOrderDocumentRules(
		orderId: number,
		documentId: string,
		uiSection: DOCUMENT_RULES_UI_SECTIONS = null
	): Observable<any> {
		const url = `${
			this.systemApiService.api.intelligenceService
		}orders/${orderId}/documents/${documentId}/rules`;
		return this.httpClient.get(uiSection ? `${url}?uiSections=${uiSection}` : url).pipe(share());
	}

	/**
	 * GET - Returns the set of rules comparer information
	 * @param orderId: number
	 * @param documentId: string
	 * @param uiSection: @see DOCUMENT_RULES_UI_SECTIONS
	 */
	getRulesComparerInformation(orderId: number): Observable<any> {
		const url = `${
			this.systemApiService.api.intelligenceService
		}orders/${orderId}/rule-comparer-information`;
		return this.httpClient.get(url).pipe(
			map(payload => {
				return payload;
			})
		);
	}

	/**
	 * POST - Creates the Rule comment.
	 * @param orderId: number
	 * @param documentId: string
	 * @param ruleName: string
	 * @param comment: string
	 */
	creatRuleComment(
		orderId: number,
		documentId: string,
		ruleName: string,
		comment: string
	): Observable<any> {
		const url = `${
			this.systemApiService.api.intelligenceService
		}orders/${orderId}/documents/${documentId}/rules/${ruleName}/notes`;
		const payload = {
			note: comment
		};
		return this.httpClient.post(url, payload).pipe(map(response => response));
	}

	/**
	 * POST - Sends rule feedback.
	 * @param {number} orderId
	 * @param {string} documentId
	 * @param {string} ruleId
	 * @param {string} feedback
	 * @returns {Observable<any>}
	 */
	sendRuleFeedBack(
		orderId: number,
		documentId: string,
		ruleId: string,
		feedback: string,
	): Observable<any> {
		const feedbackPayload: FeedbackPayload = {
			feedbackMessage: feedback,
			feedbackReason: 'Other'
		}
		const url= `${
			this.systemApiService.api.intelligenceService
		}orders/${orderId}/documents/${documentId}/rules/${ruleId}/feedback`;
		return this.httpClient.post(url, feedbackPayload);
	}

	/**
	 * GET - Returns a rule by name.
	 * @param orderId: number
	 * @param documentId: string
	 * @param ruleName: string
	 */
	getRule(orderId: number, documentId: string, ruleName: string): Observable<any> {
		const url = `${
			this.systemApiService.api.intelligenceService
		}orders/${orderId}/documents/${documentId}/rules/${ruleName}`;
		return this.httpClient.get(url).pipe(
			map(payload => {
				return payload;
			})
		);
	}

	/**
	 * Returns the Sorted Array of comments by descending most recent date.
	 * @param comments: RuleCommentModel[]
	 */
	sortRuleCommentsByDescDate(comments: RuleCommentModel[]): RuleCommentModel[] {
		return [...comments].sort((a, b) => {
			a = {
				...a,
				created: new Date(a.created)
			};
			b = {
				...b,
				created: new Date(b.created)
			};
			return b.created.getTime() - a.created.getTime();
		});
	}

	/**
	 * Returns the Sorted Array of comments by descending most recent date.
	 * @param comments: RuleCommentModel[]
	 */
	sortStipsbyDescDate(stips: RuleStipModel[]): RuleStipModel[] {
		return [...stips].sort((a, b) => {
			a = {
				...a,
				created: new Date(a.created)
			};
			b = {
				...b,
				created: new Date(b.created)
			};
			return b.created.getTime() - a.created.getTime();
		});
	}

	/**
	 * POST - Creates a new rule.
	 * @param orderId: number
	 * @param documentId: string
	 * @param messageType: RULE_MESSAGE_TYPE
	 * @param uiSection: DOCUMENT_RULES_UI_SECTIONS
	 * @param displayText: string
	 * @param message: string
	 */
	createRule(
		orderId: number,
		documentId: string,
		messageType: RULE_MESSAGE_TYPE,
		uiSection: DOCUMENT_RULES_UI_SECTIONS,
		sortId: number,
		displayText: string,
		message: string,
		targets: Array<string> = [],
		primaryTargets: Array<string> = []
	): Observable<any> {
		const url = `${
			this.systemApiService.api.intelligenceService
		}orders/${orderId}/documents/${documentId}/rules`;
		const payload = {
			messageType: messageType,
			uiSection: uiSection,
			displayText: displayText,
			message: message,
			targets: targets,
			primaryTargets: primaryTargets,
			severity: 0,
			SortId: sortId
		};
		return this.httpClient.post(url, payload);
	}

	/**
	 * PUT - Update rule status.
	 * @param orderId
	 * @param documentId
	 * @param ruleName
	 * @param status
	 */
	updateRuleStatus(
		orderId: number,
		documentId: string,
		ruleName: string,
		status: RULE_STATUS
	): Observable<any> {
		const url = `${
			this.systemApiService.api.intelligenceService
		}orders/${orderId}/documents/${documentId}/rules/${ruleName}`;
		const payload = {
			status: status
		};
		return this.httpClient.put(url, payload);
	}

	/**
	 * GET - Returns the available stips for the rule.
	 * @param ruleName: string
	 */
	getStips(ruleName: string): Observable<any> {
		return this.httpClient.get(
			`${this.systemApiService.api.intelligenceService}stipulations/search?ruleName=${ruleName}`
		);
	}

	/**
	 * POST - Creates a new stip
	 * @param orderId: number
	 * @param documentId: string
	 * @param ruleName: string
	 * @param ruleStip: {id: string, stipulation: string, overwritten: boolean}
	 */
	createStip(
		orderId: number,
		documentId: string,
		ruleName: string,
		ruleStip: { id: string; stipulation: string; overwritten: boolean }
	): Observable<any> {
		return this.httpClient.post(
			`${
				this.systemApiService.api.intelligenceService
			}orders/${orderId}/documents/${documentId}/rules/${ruleName}/stip`,
			ruleStip
		);
	}

	/**
	 * Set whether an ui section rule can allow save a order
	 * @param uiSection
	 * @param canSave
	 */
	setRulesStatusCanSave(
		uiSection: DOCUMENT_RULES_UI_SECTIONS,
		canSave: boolean,
		check: number
	): void {
		this.store.dispatch(new SetRulesCheckedRules({ uiSection, canSave, check }));
	}

	/**
	 * updated the rules checked by section
	 * @param uiSection
	 * @param ruleCheck
	 */
	updateRuleChecked(uiSection: string, ruleCheck: number): void {
		uiSection = uiSection.toLocaleLowerCase();
		this.store.dispatch(new UpdateRulesChecked({ uiSection, ruleCheck }));
	}

	/**
	 * get the rule set available for current order
	 * @param orderId
	 * @param documentId
	 */
	getAvailableRuleSets(orderId: number): Observable<any> {
		return this.httpClient
			.get(
				`${
					this.systemApiService.api.intelligenceService
				}orders/${orderId}/documents/current/current-available-rule-sets`
			)
			.pipe(map(response => this.store.dispatch(new SetAvailableRuleSet(response))));
	}

	/**
	 * get rules to be selected
	 * @param orderId
	 */
	getRulesSets(orderId: number): Observable<any> {
		return this.httpClient
			.get(
				`${
					this.systemApiService.api.intelligenceService
				}orders/${orderId}/documents/current/rule-sets`
			)
			.pipe(map(response => response));
	}

	/**
	 * forces rule set execution for current order
	 * @param orderId
	 * @param documentId
	 * @param ruleSets
	 */
	forceExecutionRuleSets(orderId: number, documentId: string, ruleSets: string): Observable<any> {
		return this.httpClient
			.post(
				`${
					this.systemApiService.api.intelligenceService
				}orders/${orderId}/documents/${documentId}/rules/forceExecution`,
				{ ruleSets }
			)
			.pipe(map(response => response));
	}

	/**
	 * Fetches post-revision details for a given revision.
	 * @param orderId 
	 * @param documentId 
	 * @param revisionId 
	 * @returns 
	 */
	getPostRevision(orderId: number, documentId: string, revisionId: string): Observable<PostRevisionModel> {
		return this.httpClient.post<PostRevisionModel>(
			`${this.systemApiService.api.intelligenceService}orders/${orderId}/documents/${documentId}/post-revisions/${revisionId}`,
			{}
		);
	}

	/**
	 * DELETE - Bulk delete a set of comments form a rule.
	 * @param orderId: number
	 * @param documentId: string
	 * @param ruleName: string
	 * @param comments: RuleCommentModel[] x
	 */
	deleteComments(
		orderId: number,
		documentId: string,
		ruleName: string,
		comments: RuleCommentModel[]
	): Observable<any> {
		const observables = [];
		comments.forEach(comment => {
			observables.push(this.deleteComment(orderId, documentId, ruleName, comment.id));
		});
		return forkJoin(observables);
	}

	/**
	 * DELETE - Deletes a single comment from a rule.
	 * @param orderId: number
	 * @param documentId: string
	 * @param ruleName: string
	 * @param commentId: string
	 */
	deleteComment(
		orderId: number,
		documentId: string,
		ruleName: string,
		commentId: string
	): Observable<any> {
		return this.httpClient
			.delete(
				`${
					this.systemApiService.api.intelligenceService
				}orders/${orderId}/documents/${documentId}/rules/${ruleName}/notes/${commentId}`
			)
			.pipe(map(response => response));
	}

	/**
	 * DELETE - Bulk delete a set of stips form a rule.
	 * @param orderId: number
	 * @param documentId: string
	 * @param ruleName: string
	 * @param stips: RuleStipModel[]
	 */
	deleteStips(
		orderId: number,
		documentId: string,
		ruleName: string,
		stips: RuleStipModel[]
	): Observable<any> {
		const observables = [];
		// remove duplicates by id to prevent BE errors.
		stips = uniqBy(stips, 'id');
		stips.forEach(stip => {
			observables.push(this.deleteStip(orderId, documentId, ruleName, stip.id));
		});
		return forkJoin(observables);
	}

	/**
	 * DELETE - Deletes a single stip from a rule.
	 * @param orderId: number
	 * @param documentId: string
	 * @param ruleName: string
	 * @param stipId: string
	 */
	deleteStip(
		orderId: number,
		documentId: string,
		ruleName: string,
		stipId: string
	): Observable<any> {
		return this.httpClient
			.delete(
				`${
					this.systemApiService.api.intelligenceService
				}orders/${orderId}/documents/${documentId}/rules/${ruleName}/stip/${stipId}`
			)
			.pipe(map(response => response));
	}

	/**
	 * Get the Rule UISECTION with how many rule left to check
	 * @param violation Array<any>
	 */
	getRulesSaveStatus(violation: Array<any>): Array<any> {
		const ruleSaveStatus = [];
		Object.keys(DOCUMENT_RULES_UI_SECTIONS).map(ui => {
			if (ui !== DOCUMENT_RULES_UI_SECTIONS.REVISIONS) {
				const uis = ui.toLowerCase();
				const rev = violation.filter(
					r => r.uiSection.toLowerCase().includes(uis) && r.status === RULE_STATUS.NOT_REVIEWED
				);
				const uiSection = <DOCUMENT_RULES_UI_SECTIONS>ui;
				ruleSaveStatus.push({ uiSection, canSave: !rev.length, check: rev.length });
			}
		});

		this.store.dispatch(new UpdateOrderRulesChecked(ruleSaveStatus));
		return ruleSaveStatus;
	}

	/**
	 * Add metadata to the Violation if the Rule contains multiple section
	 * and this sections exists
	 * @param violation Array<any>
	 */
	public setRuleMultipleSection(violations: Array<any>): Array<any> {
		const v =  violations.map((violation: any) => {
			return this.setUiSection(violation);
		});
		return v;
	}

	/**
	 * Set UI section per Rule, so it can be used separately
	 * @param violation
	 */
	public setUiSection(violation: any): any {
		const multipleUiSections = [];
		// Not all Rules had a uiSection

		if (violation.uiSection) {
			const uiSections = violation.uiSection.split('|');
			if (uiSections.length > 1) {
				uiSections.forEach((uiSection: any) => {

					const processedRuleUiSection = ObjectUtils.removeStringWhitespace(
						uiSection.toUpperCase()
					);
					const navUiSection = Object.keys(DOCUMENT_RULES_UI_SECTIONS).find((ui: string) => {
						return ui.toUpperCase() === processedRuleUiSection;
					});
					if (navUiSection) {
						const multiple = this.addMultipleSection(uiSection);
						if (multiple !== null) {
							multipleUiSections.push(multiple);
						}
					}
				});
			} else {
				const multiple = this.addMultipleSection(violation.uiSection);
				if (multiple !== null) {
					multipleUiSections.push(multiple);
				}
			}
		}

		violation = {
			...violation,
			multipleUiSections
		};
		return violation;
	}

	/**
	 * Prepare the Path and real Name of the Section
	 * @param uiSection
	 */
	private addMultipleSection(uiSection: string): any {
		const navItems = this.store.selectSnapshot(UserContextState.getNavItems);
		const navItem = navItems.find((nav: any) => {
			return (
				nav.path &&
				(nav.path.toUpperCase() === uiSection.toUpperCase() ||
					nav.path.toUpperCase() === ObjectUtils.removeStringWhitespace(uiSection.toUpperCase()))
			);
		});
		if (navItem) {
			return {
				uiSection: uiSection,
				path: navItem.path,
				label: navItem.label
			};
		}
		return null;
	}

	/**
	 * Get the Rule UISECTION with the new rules to include in the status canSave
	 * @param violation Array<any>
	 */
	getAdditionalRulesAndUpdateStatus(violation: Array<any>): void {
		Object.keys(DOCUMENT_RULES_UI_SECTIONS).map(ui => {
			if (ui !== DOCUMENT_RULES_UI_SECTIONS.REVISIONS) {
				const uis = ui.toLowerCase();
				const re = violation.filter(
					r => r.uiSection.toLowerCase().includes(uis) && r.status === RULE_STATUS.NOT_REVIEWED
				);
				if (re.length) {
					const uiS = <DOCUMENT_RULES_UI_SECTIONS>ui;
					this.setRulesStatusCanSave(uiS, !re.length, re.length);
				}
			}
		});
	}
}
